import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import 'echarts/lib/component/title';
import 'echarts/lib/component/tooltip';
import { useTheme } from '../../../../theme-context';
import NoDataOverlay from '../components/NoDataLayer';

type IssuerRow = {
  _id: string;
  cardIssuerName: string;
  totalCount: number;
  totalValue: number;

  reconciledCount: number;
  reconciledValue: number;

  potentiallyMatchCount: number;
  potentiallyMatchValue: number;

  unreconciledCount: number;
  unreconciledValue: number;

  voidedCount: number;
  voidedValue: number;
};

interface IBarChart {
  data: IssuerRow[];
}

const CardStackedBarChart = ({ data }: IBarChart) => {
  const { theme } = useTheme();

  const formatText = (text: string | null | undefined): string => {
    if (!text) return '';
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const sortedData = useMemo(() => {
    const collator = new Intl.Collator(undefined, {
      sensitivity: 'base',
      numeric: true,
    });

    const list = (data ?? []).slice();
    const isOthers = (row: IssuerRow) =>
      (row.cardIssuerName || row._id || '').trim().toLowerCase() === 'others';

    const others = list.filter(isOthers);
    const rest = list
      .filter(row => !isOthers(row))
      .sort((a, b) =>
        collator.compare(
          formatText(a.cardIssuerName || a._id || ''),
          formatText(b.cardIssuerName || b._id || '')
        )
      );

    return [...rest, ...others];
  }, [data]);

  const categories = useMemo(
    () => (sortedData ?? []).map(d => formatText(d.cardIssuerName || d._id || '')),
    [sortedData]
  );

  const hasData = useMemo(() => {
    if (!sortedData?.length) return false;
    return sortedData.some(
      d =>
        (d?.reconciledCount ?? 0) +
          (d?.potentiallyMatchCount ?? 0) +
          (d?.unreconciledCount ?? 0) +
          (d?.voidedCount ?? 0) >
        0
    );
  }, [sortedData]);

  const counts = useMemo(
    () => ({
      Reconciled: (sortedData ?? []).map(d => d?.reconciledCount ?? 0),
      'Potential Match': (sortedData ?? []).map(d => d?.potentiallyMatchCount ?? 0),
      Unreconciled: (sortedData ?? []).map(d => d?.unreconciledCount ?? 0),
      Voided: (sortedData ?? []).map(d => d?.voidedCount ?? 0),
    }),
    [sortedData]
  );

  const values = useMemo(
    () => ({
      Reconciled: (sortedData ?? []).map(d => d?.reconciledValue ?? 0),
      'Potential Match': (sortedData ?? []).map(d => d?.potentiallyMatchValue ?? 0),
      Unreconciled: (sortedData ?? []).map(d => d?.unreconciledValue ?? 0),
      Voided: (sortedData ?? []).map(d => d?.voidedValue ?? 0),
    }),
    [sortedData]
  );

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2,
    }).format(n ?? 0);

  const formatInt = (n: number) => (n ?? 0).toLocaleString(undefined);

  const textColor = theme === 'dark' ? '#e4e4e4' : '#252B37';

  const getOptionBar = () => ({
    grid: { left: '3%', right: '3%', bottom: 100, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any[]) => {
        if (!params?.length) return '';
        const idx = params[0].dataIndex;
        const issuer = params[0].axisValueLabel ?? '';

        let totalCount = 0;
        let totalValue = 0;

        const lines: string[] = [
          `<div style="margin-bottom:6px;"><b>${formatText(issuer)}</b></div>`,
        ];

        params.forEach(p => {
          const name = p.seriesName as keyof typeof counts;
          const c = counts[name][idx] ?? 0;
          const v = values[name][idx] ?? 0;

          totalCount += c;
          totalValue += v;

          lines.push(
            `<div>${p.marker}${name}: <b>${formatInt(c)}</b> &nbsp;•&nbsp; <b>${formatCurrency(
              v
            )}</b></div>`
          );
        });

        lines.push(
          `<div style="margin-top:6px;border-top:1px solid rgba(0,0,0,0.1);padding-top:6px;">
            Total: <b>${formatInt(totalCount)}</b> &nbsp;•&nbsp; <b>${formatCurrency(
              totalValue
            )}</b>
          </div>`
        );

        return lines.join('');
      },
    },
    legend: {
      data: ['Reconciled', 'Potential Match', 'Unreconciled', 'Voided'],
      textStyle: { color: textColor },
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        interval: 0,
        rotate: 0,
        margin: 12,
        width: 110,
        overflow: 'break',
        lineHeight: 14,
        formatter: (value: string) => {
          const style = value?.length > 18 ? 'small' : 'normal';
          return `{${style}|${value}}`;
        },
        rich: {
          normal: { fontSize: 12, color: textColor },
          small: { fontSize: 10, color: textColor },
        },
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: textColor },
    },
    series: [
      {
        name: 'Reconciled',
        type: 'bar',
        stack: 'total',
        data: counts.Reconciled,
        color: '#10B981',
        itemStyle: {
          opacity: hasData ? 1 : 0.4,
          borderColor: '#fff',
          borderWidth: 1,
        },
      },
      {
        name: 'Potential Match',
        type: 'bar',
        stack: 'total',
        data: counts['Potential Match'],
        color: '#F59E0B',
        itemStyle: {
          opacity: hasData ? 1 : 0.4,
          borderColor: '#fff',
          borderWidth: 1,
        },
      },
      {
        name: 'Unreconciled',
        type: 'bar',
        stack: 'total',
        data: counts.Unreconciled,
        color: '#EF4444',
        itemStyle: {
          opacity: hasData ? 1 : 0.4,
          borderColor: '#fff',
          borderWidth: 1,
        },
      },
      {
        name: 'Voided',
        type: 'bar',
        stack: 'total',
        data: counts.Voided,
        color: '#545863',
        itemStyle: {
          opacity: hasData ? 1 : 0.4,
          borderColor: '#fff',
          borderWidth: 1,
        },
      },
    ],
  });

  return (
    <div className="relative h-[400px] w-full">
      <ReactEcharts option={getOptionBar()} style={{ height: 400, width: '100%' }} />
      {!hasData && <NoDataOverlay />}
    </div>
  );
};

export default CardStackedBarChart;
